import React, { useState, useMemo } from "react";
import type { MenuItem } from "../../data/menuData";
import type { Recipe } from "../../data/recipes/types";
import type { AdminData } from "./AdminLayout";

interface Props {
  data: AdminData;
  resetData: () => void;
}

// ---------- Validation ----------

interface ValidationResult {
  errors: string[];
  warnings: string[];
}

function validate(data: AdminData): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Duplicate IDs
  const idCounts = new Map<string, number>();
  for (const item of data.menuItems) {
    idCounts.set(item.id, (idCounts.get(item.id) || 0) + 1);
  }
  for (const [id, count] of idCounts) {
    if (count > 1)
      errors.push(`Duplicate menu item ID: "${id}" (${count} times)`);
  }

  // Recipe references
  for (const item of data.menuItems) {
    if (item.recipeId && !(item.recipeId in data.recipes)) {
      errors.push(
        `Item "${item.id}" references non-existent recipe "${item.recipeId}"`
      );
    }
  }

  // Submenu labels
  const usedSubmenus = new Set<string>();
  for (const item of data.menuItems) {
    if (item.submenu) usedSubmenus.add(item.submenu);
  }
  for (const submenu of usedSubmenus) {
    if (!(submenu in data.submenuLabels)) {
      errors.push(`Submenu "${submenu}" used by items but has no label`);
    }
  }

  // Config submenus have labels
  for (const [loc, sections] of Object.entries(data.menuConfigs)) {
    for (const [section, config] of Object.entries(sections)) {
      for (const sub of config.submenus) {
        if (!(sub in data.submenuLabels)) {
          errors.push(
            `Submenu "${sub}" in ${loc}/${section} config has no label`
          );
        }
      }
    }
  }

  // Required fields
  for (const item of data.menuItems) {
    if (!item.id) errors.push("Menu item missing ID");
    if (item.price <= 0)
      errors.push(`Item "${item.id}" has invalid price: ${item.price}`);
    if (!item.recipeId && !item.name)
      errors.push(`Item "${item.id}" has neither recipeId nor name`);
  }

  // Empty submenus (warning)
  for (const [loc, sections] of Object.entries(data.menuConfigs)) {
    for (const [section, config] of Object.entries(sections)) {
      for (const sub of config.submenus) {
        const hasItems = data.menuItems.some(
          (item) =>
            item.submenu === sub &&
            (item.location === loc || item.location === "both")
        );
        if (!hasItems)
          warnings.push(`Submenu "${sub}" in ${loc}/${section} has no items`);
      }
    }
  }

  return { errors, warnings };
}

// ---------- TS File Generators ----------

function formatPrice(price: number): string {
  if (Number.isInteger(price)) return price.toFixed(1);
  return price.toString();
}

function generateMenuDataTS(data: AdminData): string {
  const lines: string[] = [];

  lines.push(`export interface MenuItem {`);
  lines.push(`  id: string;`);
  lines.push(`  name?: string; // Optional when using recipeId`);
  lines.push(`  nameFr?: string;`);
  lines.push(`  nameKey?: string; // Optional when using recipeId`);
  lines.push(`  description?: string; // Optional when using recipeId`);
  lines.push(`  descriptionFr?: string;`);
  lines.push(`  descriptionKey?: string; // Optional when using recipeId`);
  lines.push(`  price: number;`);
  lines.push(
    `  category: "specials" | "lunch" | "dinner" | "grocery" | "new-arrivals";`
  );
  lines.push(`  location: "montreal" | "delson" | "both";`);
  lines.push(`  submenu?: string;`);
  lines.push(`  image?: string;`);
  lines.push(`  popular?: boolean;`);
  lines.push(`  spicy?: boolean;`);
  lines.push(`  vegetarian?: boolean;`);
  lines.push(`  vegan?: boolean;`);
  lines.push(`  glutenFree?: boolean;`);
  lines.push(`  new?: boolean;`);
  lines.push(
    `  recipeId?: string; // Reference to Recipe Book for shared dish information`
  );
  lines.push(`}`);
  lines.push(``);
  lines.push(`export const menuItems: MenuItem[] = [`);

  // Group by location -> category
  const groupMap = new Map<
    string,
    { location: string; category: string; items: MenuItem[] }
  >();
  for (const item of data.menuItems) {
    const key = `${item.location}-${item.category}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        location: item.location,
        category: item.category,
        items: [],
      });
    }
    groupMap.get(key)!.items.push(item);
  }

  for (const group of groupMap.values()) {
    const locUpper = group.location.toUpperCase();
    const catUpper = group.category.toUpperCase().replace("-", " ");
    lines.push(`  // ============================================`);
    lines.push(`  // ${locUpper} - ${catUpper}`);
    lines.push(`  // ============================================`);

    for (const item of group.items) {
      lines.push(`  {`);
      lines.push(`    id: ${JSON.stringify(item.id)},`);
      if (item.recipeId)
        lines.push(`    recipeId: ${JSON.stringify(item.recipeId)},`);
      if (item.name) lines.push(`    name: ${JSON.stringify(item.name)},`);
      if (item.nameFr)
        lines.push(`    nameFr: ${JSON.stringify(item.nameFr)},`);
      if (item.nameKey)
        lines.push(`    nameKey: ${JSON.stringify(item.nameKey)},`);
      if (item.description)
        lines.push(`    description: ${JSON.stringify(item.description)},`);
      if (item.descriptionFr)
        lines.push(
          `    descriptionFr: ${JSON.stringify(item.descriptionFr)},`
        );
      if (item.descriptionKey)
        lines.push(
          `    descriptionKey: ${JSON.stringify(item.descriptionKey)},`
        );
      lines.push(`    price: ${formatPrice(item.price)},`);
      lines.push(`    category: ${JSON.stringify(item.category)},`);
      lines.push(`    location: ${JSON.stringify(item.location)},`);
      if (item.submenu)
        lines.push(`    submenu: ${JSON.stringify(item.submenu)},`);
      if (item.popular !== undefined)
        lines.push(`    popular: ${item.popular},`);
      if (item.spicy !== undefined) lines.push(`    spicy: ${item.spicy},`);
      if (item.vegetarian !== undefined)
        lines.push(`    vegetarian: ${item.vegetarian},`);
      if (item.vegan !== undefined) lines.push(`    vegan: ${item.vegan},`);
      if (item.glutenFree !== undefined)
        lines.push(`    glutenFree: ${item.glutenFree},`);
      if (item.new !== undefined) lines.push(`    new: ${item.new},`);
      lines.push(`  },`);
    }
    lines.push(``);
  }

  lines.push(`];`);
  lines.push(``);

  // Helper functions
  lines.push(`// Helper functions`);
  lines.push(`export const getMenuItemsByCategory = (`);
  lines.push(`  category: MenuItem["category"],`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.category === category &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const getPopularItems = (`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.popular &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(
    `export const getNewItems = (location?: MenuItem["location"]): MenuItem[] => {`
  );
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.new &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const getMenuItemsByLocation = (`);
  lines.push(`  location: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(
    `    (item) => item.location === location || item.location === "both",`
  );
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const searchMenuItems = (`);
  lines.push(`  query: string,`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  const lowerQuery = query.toLowerCase();`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      (item.name?.toLowerCase().includes(lowerQuery) ||`);
  lines.push(`        false ||`);
  lines.push(`        item.description?.toLowerCase().includes(lowerQuery) ||`);
  lines.push(`        false) &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(
    `export const getMenuItemById = (id: string): MenuItem | undefined => {`
  );
  lines.push(`  return menuItems.find((item) => item.id === id);`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const getVegetarianItems = (`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.vegetarian &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const getGlutenFreeItems = (`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.glutenFree &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`export const getMenuItemsBySubmenu = (`);
  lines.push(`  submenu: string,`);
  lines.push(`  location?: MenuItem["location"],`);
  lines.push(`): MenuItem[] => {`);
  lines.push(`  return menuItems.filter(`);
  lines.push(`    (item) =>`);
  lines.push(`      item.submenu === submenu &&`);
  lines.push(`      (location`);
  lines.push(
    `        ? item.location === location || item.location === "both"`
  );
  lines.push(`        : true),`);
  lines.push(`  );`);
  lines.push(`};`);
  lines.push(``);
  lines.push(`// Category labels`);
  lines.push(`export const categoryLabels = {`);
  lines.push(`  specials: "Specials",`);
  lines.push(`  lunch: "Lunch",`);
  lines.push(`  dinner: "Dinner",`);
  lines.push(`  grocery: "Grocery",`);
  lines.push(`  "new-arrivals": "New Arrivals",`);
  lines.push(`};`);
  lines.push(``);
  lines.push(
    `export const getCategoryLabel = (category: MenuItem["category"]): string => {`
  );
  lines.push(`  return categoryLabels[category] || category;`);
  lines.push(`};`);
  lines.push(``);
  lines.push(
    `// Re-export submenu labels and helper from menuConfig (single source of truth)`
  );
  lines.push(
    `export { submenuLabels, getSubmenuLabel } from "./menuConfig";`
  );
  lines.push(``);
  lines.push(`// end of script`);

  return lines.join("\n") + "\n";
}

function serializeSubmenus(submenus: readonly string[]): string {
  if (submenus.length <= 2) {
    return `[${submenus.map((s) => JSON.stringify(s)).join(", ")}] as const`;
  }
  const items = submenus
    .map((s) => `        ${JSON.stringify(s)},`)
    .join("\n");
  return `[\n${items}\n      ] as const`;
}

function generateMenuConfigTS(data: AdminData): string {
  const lines: string[] = [];

  lines.push(
    `// Menu configuration: Drives navigation, submenus, labels, and rendering order.`
  );
  lines.push(
    `// Enables data-driven refactors in MenuPage.tsx without hardcoding.`
  );
  lines.push(``);
  lines.push(`export type Location = "montreal" | "delson";`);
  lines.push(``);
  lines.push(`export interface AddOn {`);
  lines.push(`  name: string;`);
  lines.push(`  nameFr: string;`);
  lines.push(`  price: number;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export interface SubmenuAddOns {`);
  lines.push(`  submenu: string;`);
  lines.push(`  addOns: AddOn[];`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export interface SubmenuInfo {`);
  lines.push(`  submenu: string;`);
  lines.push(`  info: string;`);
  lines.push(`  infoFr: string;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`interface SectionConfig {`);
  lines.push(`  label: {`);
  lines.push(`    en: string;`);
  lines.push(`    fr: string;`);
  lines.push(`  };`);
  lines.push(`  submenus: readonly string[];`);
  lines.push(`  order: number;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export type MenuConfigs = {`);
  lines.push(`  [K in Location]: Record<string, SectionConfig>;`);
  lines.push(`};`);
  lines.push(``);

  // menuConfigs
  lines.push(`export const menuConfigs: MenuConfigs = {`);
  for (const loc of ["montreal", "delson"] as const) {
    lines.push(`  ${loc}: {`);
    const sections = data.menuConfigs[loc];
    for (const [sectionKey, config] of Object.entries(sections)) {
      lines.push(`    ${JSON.stringify(sectionKey)}: {`);
      lines.push(
        `      label: { en: ${JSON.stringify(config.label.en)}, fr: ${JSON.stringify(config.label.fr)} },`
      );
      lines.push(
        `      submenus: ${serializeSubmenus(config.submenus)},`
      );
      lines.push(`      order: ${config.order},`);
      lines.push(`    },`);
    }
    lines.push(`  },`);
  }
  lines.push(`} as const;`);
  lines.push(``);

  // submenuLabels
  lines.push(
    `// Comprehensive submenu labels - ALL submenus must be listed here`
  );
  lines.push(
    `export const submenuLabels: Record<string, { en: string; fr: string }> = {`
  );
  for (const [key, val] of Object.entries(data.submenuLabels)) {
    lines.push(
      `  ${JSON.stringify(key)}: { en: ${JSON.stringify(val.en)}, fr: ${JSON.stringify(val.fr)} },`
    );
  }
  lines.push(`};`);
  lines.push(``);

  // Helper functions (static)
  lines.push(`export type SectionName<L extends Location> = keyof MenuConfigs[L];`);
  lines.push(``);
  lines.push(`export function getSectionConfig<L extends Location>(`);
  lines.push(`  location: L,`);
  lines.push(`  section: SectionName<L>,`);
  lines.push(`): MenuConfigs[L][SectionName<L>] {`);
  lines.push(`  const config = menuConfigs[location][section];`);
  lines.push(`  if (!config) {`);
  lines.push(`    throw new Error(`);
  lines.push(
    `      \`Invalid section "\${String(section)}" for location "\${location}"\`,`
  );
  lines.push(`    );`);
  lines.push(`  }`);
  lines.push(`  return config;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getNavSections<L extends Location>(`);
  lines.push(`  location: L,`);
  lines.push(`): SectionName<L>[] {`);
  lines.push(`  return Object.entries(menuConfigs[location])`);
  lines.push(`    .sort(([, a], [, b]) => a.order - b.order)`);
  lines.push(`    .map(([key]) => key as SectionName<L>);`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getSectionLabel<L extends Location>(`);
  lines.push(`  location: L,`);
  lines.push(`  section: SectionName<L>,`);
  lines.push(`  lang: "en" | "fr" = "en",`);
  lines.push(`): string {`);
  lines.push(`  return getSectionConfig(location, section).label[lang];`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getSubmenuLabel(`);
  lines.push(`  submenu: string,`);
  lines.push(`  language: "en" | "fr" = "en",`);
  lines.push(`): string {`);
  lines.push(`  return submenuLabels[submenu]?.[language] || submenu;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getSubmenusForSection<L extends Location>(`);
  lines.push(`  location: L,`);
  lines.push(`  section: SectionName<L>,`);
  lines.push(`): readonly string[] {`);
  lines.push(`  return getSectionConfig(location, section).submenus;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function isValidSubmenu(submenu: string): boolean {`);
  lines.push(`  return submenu in submenuLabels;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getAllSubmenus(): string[] {`);
  lines.push(`  const allSubmenus = new Set<string>();`);
  lines.push(
    `  for (const location of Object.keys(menuConfigs) as Location[]) {`
  );
  lines.push(
    `    for (const section of Object.keys(menuConfigs[location])) {`
  );
  lines.push(`      const config = menuConfigs[location][section];`);
  lines.push(`      for (const submenu of config.submenus) {`);
  lines.push(`        allSubmenus.add(submenu);`);
  lines.push(`      }`);
  lines.push(`    }`);
  lines.push(`  }`);
  lines.push(`  return Array.from(allSubmenus);`);
  lines.push(`}`);
  lines.push(``);

  // submenuAddOns
  lines.push(
    `// Add-ons configuration for specific submenus`
  );
  lines.push(
    `export const submenuAddOns: Record<Location, Record<string, SubmenuAddOns>> = {`
  );
  for (const loc of ["montreal", "delson"] as const) {
    lines.push(`  ${loc}: {`);
    const addOns = data.submenuAddOns[loc];
    for (const [key, config] of Object.entries(addOns)) {
      lines.push(`    ${JSON.stringify(key)}: {`);
      lines.push(`      submenu: ${JSON.stringify(config.submenu)},`);
      lines.push(`      addOns: [`);
      for (const addon of config.addOns) {
        lines.push(
          `        { name: ${JSON.stringify(addon.name)}, nameFr: ${JSON.stringify(addon.nameFr)}, price: ${addon.price} },`
        );
      }
      lines.push(`      ],`);
      lines.push(`    },`);
    }
    lines.push(`  },`);
  }
  lines.push(`};`);
  lines.push(``);

  // submenuInfos
  lines.push(`// Submenu info/notes configuration`);
  lines.push(
    `export const submenuInfos: Record<Location, Record<string, SubmenuInfo>> = {`
  );
  for (const loc of ["montreal", "delson"] as const) {
    lines.push(`  ${loc}: {`);
    const infos = data.submenuInfos[loc];
    for (const [key, config] of Object.entries(infos)) {
      lines.push(`    ${JSON.stringify(key)}: {`);
      lines.push(`      submenu: ${JSON.stringify(config.submenu)},`);
      lines.push(`      info: ${JSON.stringify(config.info)},`);
      lines.push(`      infoFr: ${JSON.stringify(config.infoFr)},`);
      lines.push(`    },`);
    }
    lines.push(`  },`);
  }
  lines.push(`};`);
  lines.push(``);

  lines.push(`export function getSubmenuAddOns(`);
  lines.push(`  location: Location,`);
  lines.push(`  section: string,`);
  lines.push(`  submenu: string,`);
  lines.push(`): AddOn[] | null {`);
  lines.push(`  const key = \`\${section}-\${submenu}\`;`);
  lines.push(`  const addOnsConfig = submenuAddOns[location][key];`);
  lines.push(`  if (addOnsConfig && addOnsConfig.submenu === submenu) {`);
  lines.push(`    return addOnsConfig.addOns;`);
  lines.push(`  }`);
  lines.push(`  return null;`);
  lines.push(`}`);
  lines.push(``);
  lines.push(`export function getSubmenuInfo(`);
  lines.push(`  location: Location,`);
  lines.push(`  section: string,`);
  lines.push(`  submenu: string,`);
  lines.push(`): { info: string; infoFr: string } | null {`);
  lines.push(`  const key = \`\${section}-\${submenu}\`;`);
  lines.push(`  const infoConfig = submenuInfos[location][key];`);
  lines.push(`  if (infoConfig && infoConfig.submenu === submenu) {`);
  lines.push(
    `    return { info: infoConfig.info, infoFr: infoConfig.infoFr };`
  );
  lines.push(`  }`);
  lines.push(`  return null;`);
  lines.push(`}`);

  return lines.join("\n") + "\n";
}

function generateRecipeFileTS(
  exportName: string,
  recipes: Record<string, Recipe>
): string {
  const lines: string[] = [];
  lines.push(`import { Recipe } from "./types";`);
  lines.push(``);
  lines.push(`export const ${exportName}: Record<string, Recipe> = {`);

  for (const [id, recipe] of Object.entries(recipes)) {
    const keyStr = id.includes("-") ? JSON.stringify(id) : id;
    lines.push(`  ${keyStr}: {`);
    lines.push(`    id: ${JSON.stringify(recipe.id)},`);
    lines.push(`    name: ${JSON.stringify(recipe.name)},`);
    lines.push(`    nameFr: ${JSON.stringify(recipe.nameFr)},`);
    lines.push(`    nameKey: ${JSON.stringify(recipe.nameKey)},`);
    lines.push(`    description: ${JSON.stringify(recipe.description)},`);
    lines.push(
      `    descriptionFr: ${JSON.stringify(recipe.descriptionFr)},`
    );
    lines.push(
      `    descriptionKey: ${JSON.stringify(recipe.descriptionKey)},`
    );
    if (recipe.vegetarian !== undefined)
      lines.push(`    vegetarian: ${recipe.vegetarian},`);
    if (recipe.vegan !== undefined)
      lines.push(`    vegan: ${recipe.vegan},`);
    if (recipe.glutenFree !== undefined)
      lines.push(`    glutenFree: ${recipe.glutenFree},`);
    if (recipe.spicy !== undefined)
      lines.push(`    spicy: ${recipe.spicy},`);
    lines.push(`  },`);
    lines.push(``);
  }

  lines.push(`};`);
  return lines.join("\n") + "\n";
}

// ---------- Download Helper ----------

function downloadFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------- Component ----------

// We need to categorize recipes for export. Since the admin stores them flat,
// we use the original recipe files to determine which category each recipe belongs to.
import { appetizerRecipes } from "../../data/recipes/appetizers";
import { mainDishRecipes } from "../../data/recipes/mainDishes";
import { drinksRecipes } from "../../data/recipes/drinks";
import { dessertsRecipes } from "../../data/recipes/desserts";
import { extrasRecipes } from "../../data/recipes/extras";
import { groceryRecipes } from "../../data/recipes/grocery";

const ORIGINAL_CATEGORIES: Record<string, Set<string>> = {
  appetizers: new Set(Object.keys(appetizerRecipes)),
  mainDishes: new Set(Object.keys(mainDishRecipes)),
  drinks: new Set(Object.keys(drinksRecipes)),
  desserts: new Set(Object.keys(dessertsRecipes)),
  extras: new Set(Object.keys(extrasRecipes)),
  grocery: new Set(Object.keys(groceryRecipes)),
};

function categorizeRecipes(
  allRecipes: Record<string, Recipe>
): Record<string, Record<string, Recipe>> {
  const result: Record<string, Record<string, Recipe>> = {
    appetizers: {},
    mainDishes: {},
    drinks: {},
    desserts: {},
    extras: {},
    grocery: {},
  };

  for (const [id, recipe] of Object.entries(allRecipes)) {
    let placed = false;
    for (const [cat, ids] of Object.entries(ORIGINAL_CATEGORIES)) {
      if (ids.has(id)) {
        result[cat][id] = recipe;
        placed = true;
        break;
      }
    }
    // New recipes default to mainDishes
    if (!placed) {
      result.mainDishes[id] = recipe;
    }
  }

  return result;
}

const RECIPE_FILE_MAP: Record<string, { filename: string; exportName: string }> = {
  appetizers: { filename: "appetizers.ts", exportName: "appetizerRecipes" },
  mainDishes: { filename: "mainDishes.ts", exportName: "mainDishRecipes" },
  drinks: { filename: "drinks.ts", exportName: "drinksRecipes" },
  desserts: { filename: "desserts.ts", exportName: "dessertsRecipes" },
  extras: { filename: "extras.ts", exportName: "extrasRecipes" },
  grocery: { filename: "grocery.ts", exportName: "groceryRecipes" },
};

const DataExport: React.FC<Props> = ({ data, resetData }) => {
  const [resetConfirm, setResetConfirm] = useState(false);

  const validation = useMemo(() => validate(data), [data]);
  const categorizedRecipes = useMemo(
    () => categorizeRecipes(data.recipes),
    [data.recipes]
  );

  const handleDownloadMenuData = () => {
    downloadFile("menuData.ts", generateMenuDataTS(data));
  };

  const handleDownloadMenuConfig = () => {
    downloadFile("menuConfig.ts", generateMenuConfigTS(data));
  };

  const handleDownloadRecipeFile = (category: string) => {
    const config = RECIPE_FILE_MAP[category];
    const recipes = categorizedRecipes[category];
    const content = generateRecipeFileTS(config.exportName, recipes);
    downloadFile(config.filename, content);
  };

  const handleDownloadAll = () => {
    handleDownloadMenuData();
    setTimeout(() => handleDownloadMenuConfig(), 200);
    let delay = 400;
    for (const category of Object.keys(RECIPE_FILE_MAP)) {
      setTimeout(() => handleDownloadRecipeFile(category), delay);
      delay += 200;
    }
  };

  const handleExportJSON = () => {
    const json = JSON.stringify(data, null, 2);
    downloadFile("menu-backup.json", json);
  };

  const handleImportJSON = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target?.result as string);
          if (imported.menuItems && imported.recipes && imported.menuConfigs) {
            localStorage.setItem("cava-admin-data", JSON.stringify(imported));
            window.location.reload();
          } else {
            alert("Invalid backup file format.");
          }
        } catch {
          alert("Failed to parse JSON file.");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleReset = () => {
    resetData();
    setResetConfirm(false);
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Export & Data Management</h2>
      </div>

      {/* Validation */}
      {validation.errors.length > 0 && (
        <div className="admin-validation error">
          <h4>Validation Errors ({validation.errors.length})</h4>
          <ul>
            {validation.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="admin-validation warning">
          <h4>Warnings ({validation.warnings.length})</h4>
          <ul>
            {validation.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {validation.errors.length === 0 && (
        <div className="admin-validation success">
          <h4>Validation Passed</h4>
          <p style={{ margin: 0, fontSize: "0.85rem" }}>
            All data is valid and ready to export.
          </p>
        </div>
      )}

      {/* Download All */}
      <div style={{ marginTop: "1.5rem" }}>
        <button
          className="btn btn-success"
          onClick={handleDownloadAll}
          disabled={validation.errors.length > 0}
          style={{ padding: "0.6rem 1.5rem", fontSize: "1rem" }}
        >
          Download All Files
        </button>
        <p style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.4rem" }}>
          Downloads menuData.ts, menuConfig.ts, and all recipe files. Replace
          them in <code>src/data/</code>, commit, and push to deploy.
        </p>
      </div>

      {/* Individual Files */}
      <h3 style={{ marginTop: "1.5rem" }}>Individual Files</h3>
      <div className="admin-export-grid">
        <div className="admin-export-card">
          <h4>menuData.ts</h4>
          <p>{data.menuItems.length} menu items</p>
          <button className="btn btn-primary btn-sm" onClick={handleDownloadMenuData}>
            Download
          </button>
        </div>
        <div className="admin-export-card">
          <h4>menuConfig.ts</h4>
          <p>Sections, submenus, labels, add-ons</p>
          <button className="btn btn-primary btn-sm" onClick={handleDownloadMenuConfig}>
            Download
          </button>
        </div>
        {Object.entries(RECIPE_FILE_MAP).map(([category, config]) => (
          <div key={category} className="admin-export-card">
            <h4>{config.filename}</h4>
            <p>{Object.keys(categorizedRecipes[category] || {}).length} recipes</p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleDownloadRecipeFile(category)}
            >
              Download
            </button>
          </div>
        ))}
      </div>

      {/* Backup & Restore */}
      <h3 style={{ marginTop: "1.5rem" }}>Backup & Restore</h3>
      <div className="admin-toolbar">
        <button className="btn btn-secondary" onClick={handleExportJSON}>
          Export JSON Backup
        </button>
        <button className="btn btn-secondary" onClick={handleImportJSON}>
          Import JSON Backup
        </button>
        {resetConfirm ? (
          <>
            <button className="btn btn-danger" onClick={handleReset}>
              Confirm Reset
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setResetConfirm(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            className="btn btn-danger"
            onClick={() => setResetConfirm(true)}
          >
            Reset to Original
          </button>
        )}
      </div>
      <p style={{ fontSize: "0.8rem", color: "#6b7280" }}>
        JSON backup saves/restores your admin changes. Reset discards all
        changes and reloads from the built-in data.
      </p>
    </div>
  );
};

export default DataExport;
